import { useEffect, useRef, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Heartbeat interval — must be less than the shortest idle-timeout of any
// upstream proxy (Nginx default 60s, Cloudflare ~100s, most cloud LBs 30-60s).
// 20s keeps the connection warm without wasting bandwidth.
const HEARTBEAT_MS = 20000;
// If we don't hear back from the server within this window after a ping,
// assume the socket is silently dead and reconnect.
const PONG_TIMEOUT_MS = 10000;

export function useChatSocket({ onEvent }) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const onEventRef = useRef(onEvent);

  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  useEffect(() => {
    let cancelled = false;
    // Local timer handles — kept inside the effect so cleanup captures them
    // directly (avoids stale/cleared cross-scope refs in production builds).
    let heartbeat = null;
    let pongDeadline = null;
    let reconnect = null;

    const stopHeartbeat = () => {
      if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
      if (pongDeadline) { clearTimeout(pongDeadline); pongDeadline = null; }
    };

    const startHeartbeat = (ws) => {
      stopHeartbeat();
      heartbeat = setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return;
        try { ws.send(JSON.stringify({ event: "ping", ts: Date.now() })); } catch { /* ignore */ }
        // If no pong within window → force reconnect
        if (pongDeadline) clearTimeout(pongDeadline);
        pongDeadline = setTimeout(() => {
          try { ws.close(); } catch { /* ignore */ }
        }, PONG_TIMEOUT_MS);
      }, HEARTBEAT_MS);
    };

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem("prism_token");
      if (!token) return;
      const wsUrl = BACKEND.replace(/^http/, "ws") + `/api/ws/chat?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => { setConnected(true); startHeartbeat(ws); };
      ws.onclose = () => {
        setConnected(false);
        stopHeartbeat();
        if (!cancelled) {
          reconnect = setTimeout(connect, 2000);
        }
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          // Any inbound frame counts as liveness; explicit pong clears the deadline
          if (pongDeadline) { clearTimeout(pongDeadline); pongDeadline = null; }
          if (data.event === "pong") return;
          onEventRef.current?.(data);
        } catch { /* ignore */ }
      };
    };
    connect();
    return () => {
      cancelled = true;
      if (reconnect) { clearTimeout(reconnect); reconnect = null; }
      stopHeartbeat();
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, []);

  const send = (obj) => {
    try { wsRef.current?.send(JSON.stringify(obj)); } catch { /* ignore */ }
  };
  return { connected, send };
}
